import { yupResolver } from "@hookform/resolvers/yup";
import { useEffect, useState } from "react";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";

import {
  CancelButton,
  ErrorModal,
  SubmitButton,
  SuccessModal,
} from "@components";
import { BasicInfo } from "./BasicInfo";
import { TerminationHeader } from "./TerminationHeader";
import { TerminationModal } from "./TerminationModal";
import { SupportingDocument } from "./SupportingDocument";

import { ROUTES } from "@constants/routes";
import { STATUS } from "@constants/status.hrm";

import { useGeneralInfo } from "@hooks/HRM/employee/useGeneralInfo";

import {
  convertDocumentData,
  restructureDocumentsData,
} from "@utils/documentDataConvert";
import { GetEmployeeDetails } from "@utils/getEmployeeDetails";

import { initiateTermination } from "@api/termination";
import {
  TerminationFormDataType,
  SUPPORTING_DOCUMENT,
  terminationFormValidation,
} from "@validations/HRM/termination/terminationFormValidation";

const {
  HRM: { TERMINATION },
} = ROUTES;
const { SUCCESS, FAILED, DRAFT, IN_PROGRESS, APPROVED } = STATUS;

export const TerminationDetailsBody = ({
  isDisabled,
  isCreatePage,
  showActionButton,
  showApprovalButton,
  terminationData,
  employeeMasterId,
}: {
  isDisabled: boolean;
  isCreatePage: boolean;
  showActionButton: boolean;
  showApprovalButton: boolean;
  terminationData: any;
  employeeMasterId: string | number | null;
}) => {
  const [modalTitle, setModalTitle] = useState<string>("");
  const [isCommentModalOpen, setIsCommentModalOpen] = useState<boolean>(false);
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
  const [isErrorModalOpen, setIsErrorModalOpen] = useState(false);
  const [isNavigate, setIsNavigate] = useState<boolean>(false);
  const [terminationId, setTerminationId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const navigate = useNavigate();
  const { id } = useParams();

  const { generalInfo } = useGeneralInfo(isCreatePage ? (id as any) : null);

  const { employeeName, employeePosition, employeeImage } = GetEmployeeDetails(
    terminationData.employee,
  );

  const { nameEn } = terminationData.employee || {};

  const methods = useForm<TerminationFormDataType>({
    resolver: yupResolver(terminationFormValidation),
    values: isCreatePage
      ? {
          ...generalInfo,
          deathDate: generalInfo?.dateOfDeath,
        }
      : {
          ...terminationData,
          nameEn,
          ...restructureDocumentsData(terminationData.documents),
        },
  });

  const { handleSubmit, reset } = methods;

  const onSubmit: SubmitHandler<TerminationFormDataType> = (data: any) => {
    setIsLoading(true);
    const convertedData = convertDocumentData(data, SUPPORTING_DOCUMENT);

    const submitPayload = {
      description: convertedData.description,
      terminateDate: convertedData.terminateDate,
      terminateReason: convertedData.terminateReason,
      status: DRAFT,
      employeeId: isCreatePage ? data.id : convertedData.employee.id,
      documents: convertedData.documents,
    };

    const sendForApprovalPayload = {
      status: IN_PROGRESS,
      comments: data.comments,
      reviewerId: data.reviewerId,
    };

    const approvePayload = {
      status: APPROVED,
      comments: data.comments,
      reviewerId: Number(employeeMasterId),
    };

    const payload =
      modalTitle === APPROVED
        ? approvePayload
        : modalTitle === IN_PROGRESS
        ? sendForApprovalPayload
        : submitPayload;

    initiateTermination(
      isCreatePage,
      payload,
      (response) => {
        if (response.status === SUCCESS) {
          setIsSuccessModalOpen(true);
          isCreatePage && setTerminationId(response.data.id);
          setIsNavigate(true);
        } else if (response.status === FAILED) setIsErrorModalOpen(true);
        setIsLoading(false);
      },
      id,
      modalTitle === APPROVED || modalTitle === IN_PROGRESS,
    );
  };

  // termination modal
  const openModal = (status: string) => {
    modalTitle === IN_PROGRESS && setIsCommentModalOpen(true);
    setModalTitle(status);
  };
  const closeModal = () => {
    setIsCommentModalOpen(false);
    reset();
  };

  const closeErrorModal = () => setIsErrorModalOpen(false);
  const createSuccessAction = () => {
    setIsSuccessModalOpen(false);
    reset();
    if (isCreatePage) {
      navigate(`${TERMINATION}/${terminationId}`);
    } else {
      isNavigate && navigate(TERMINATION);
    }
  };

  useEffect(() => {
    const resetStates = () => {
      setIsErrorModalOpen(false);
      setIsSuccessModalOpen(false);
      setIsCommentModalOpen(false);
      setModalTitle("");
      setIsNavigate(false);
      reset();
    };
    return resetStates();
  }, []);

  return (
    <>
      {/* MODAL SECTION */}
      <>
        <SuccessModal
          isModalOpen={isSuccessModalOpen}
          action={createSuccessAction}
        />
        <ErrorModal
          isModalOpen={isErrorModalOpen}
          closeModal={closeErrorModal}
        />
      </>
      {/* MODAL SECTION */}
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <TerminationModal
            isModalOpen={isCommentModalOpen}
            closeModal={closeModal}
            isLoading={isLoading}
          />
          {!isCreatePage && (
            <TerminationHeader
              data={{
                employeeName,
                employeePosition,
                employeeImage,
              }}
              openModal={openModal}
              showActionButton={showActionButton}
              showApprovalButton={showApprovalButton}
              isLoading={isLoading}
            />
          )}
          <div className="p-10">
            <BasicInfo isDisabled={isDisabled} />
            <SupportingDocument isDisabled={isDisabled} />
            {!isDisabled && (
              <div className="d-flex justify-content-end gap-6 border-top mt-10 pt-10">
                <CancelButton path="/termination" loading={isLoading} />
                <SubmitButton loading={isLoading} />
              </div>
            )}
          </div>
        </form>
      </FormProvider>
    </>
  );
};
